import { useEffect, useRef, useState } from "react";
import { Play, Pause, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useSettings } from "../context/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Unified bedtime speech controls.
 * Three buttons: Play / Pause / Stop. Supports:
 *  - Web Speech API (native, default)
 *  - ElevenLabs (via backend /api/tts/elevenlabs)
 *  - OpenAI TTS (via backend /api/tts/openai)
 */
export default function SpeechControls({ text }) {
  const { settings } = useSettings();
  const provider = settings.voice.provider;

  const [status, setStatus] = useState("idle"); // idle | loading | playing | paused
  const [supported, setSupported] = useState(true);

  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  useEffect(() => {
    setSupported(
      provider !== "webspeech" ||
        (typeof window !== "undefined" && "speechSynthesis" in window)
    );
    return () => {
      stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  // ----- Web Speech -----
  const speakWebSpeech = () => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "es-ES";
    u.rate = settings.voice.webSpeechRate || 0.92;
    u.pitch = settings.voice.webSpeechPitch || 1.0;
    const voices = window.speechSynthesis.getVoices();
    const es = voices.find((v) => v.lang?.toLowerCase().startsWith("es"));
    if (es) u.voice = es;
    u.onend = () => setStatus("idle");
    u.onerror = () => setStatus("idle");
    window.speechSynthesis.speak(u);
    setStatus("playing");
  };

  // ----- Audio (ElevenLabs / OpenAI) -----
  const playFromUrl = (url) => {
    const audio = new Audio(url);
    audio.onplay = () => setStatus("playing");
    audio.onpause = () => {
      if (!audio.ended) setStatus("paused");
    };
    audio.onended = () => setStatus("idle");
    audio.onerror = () => {
      setStatus("idle");
      toast.error("Error reproduciendo audio");
    };
    audioRef.current = audio;
    audio.play().catch(() => setStatus("idle"));
  };

  const fetchAndPlay = async (endpoint, body) => {
    setStatus("loading");
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.text();
        let msg = t;
        try {
          msg = JSON.parse(t).detail || t;
        } catch {
          // ignore
        }
        toast.error(`No se pudo generar la voz: ${String(msg).slice(0, 140)}`);
        setStatus("idle");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = url;
      playFromUrl(url);
    } catch (e) {
      toast.error("Error de red al generar la voz");
      setStatus("idle");
    }
  };

  // ----- Actions -----
  const onPlay = () => {
    if (!text) return;
    // If paused, resume current playback
    if (status === "paused") {
      if (provider === "webspeech") {
        window.speechSynthesis.resume();
        setStatus("playing");
      } else if (audioRef.current) {
        audioRef.current.play();
      }
      return;
    }
    if (status === "playing") return;

    // Fresh start
    if (provider === "webspeech") {
      speakWebSpeech();
      return;
    }
    if (provider === "elevenlabs") {
      const cfg = settings.voice;
      if (!cfg.elevenlabsApiKey) {
        toast.error("Configura tu API key de ElevenLabs en ajustes");
        return;
      }
      fetchAndPlay("/tts/elevenlabs", {
        text,
        api_key: cfg.elevenlabsApiKey,
        voice_id: cfg.elevenlabsVoiceId,
        model: cfg.elevenlabsModel,
      });
      return;
    }
    if (provider === "openai") {
      const cfg = settings.voice;
      if (!cfg.openaiApiKey) {
        toast.error("Configura tu API key de OpenAI en ajustes");
        return;
      }
      fetchAndPlay("/tts/openai", {
        text,
        api_key: cfg.openaiApiKey,
        voice: cfg.openaiVoice,
        model: cfg.openaiModel,
      });
    }
  };

  const onPause = () => {
    if (provider === "webspeech") {
      if (status === "playing") {
        window.speechSynthesis.pause();
        setStatus("paused");
      }
    } else if (audioRef.current && status === "playing") {
      audioRef.current.pause();
    }
  };

  const stopAll = () => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {
        // ignore
      }
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  };

  const onStop = () => {
    stopAll();
    setStatus("idle");
  };

  if (provider === "webspeech" && !supported) return null;

  const isPlaying = status === "playing";
  const isPaused = status === "paused";
  const isLoading = status === "loading";

  return (
    <div className="flex items-center gap-2" data-testid="speech-controls">
      <Button
        type="button"
        onClick={onPlay}
        disabled={isLoading || isPlaying}
        data-testid="speak-play-button"
        className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-5 py-2 shadow-[0_0_20px_rgba(251,191,36,0.35)] disabled:opacity-60"
      >
        <Play className="w-4 h-4 mr-2" />
        {isLoading
          ? "Cargando..."
          : isPaused
          ? "Reanudar"
          : "Reproducir"}
      </Button>
      <Button
        type="button"
        onClick={onPause}
        disabled={!isPlaying}
        data-testid="speak-pause-button"
        variant="ghost"
        className="text-slate-200 hover:bg-white/10 rounded-full disabled:opacity-40"
      >
        <Pause className="w-4 h-4 mr-2" /> Pausar
      </Button>
      <Button
        type="button"
        onClick={onStop}
        disabled={status === "idle"}
        data-testid="speak-stop-button"
        variant="ghost"
        className="text-slate-200 hover:bg-white/10 rounded-full disabled:opacity-40"
      >
        <Square className="w-4 h-4 mr-2" /> Detener
      </Button>
    </div>
  );
}
